import React, { useEffect, useState } from "react";
import { ScrollContainer } from "react-indiana-drag-scroll";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import API from "../../services/api";
import config from "../../config";
import Paginator from "../../shared/Paginator";
import {
  SlInput,
  SlIcon,
  SlSelect,
  SlCheckbox,
  SlOption,
  SlButton,
} from "@shoelace-style/shoelace/dist/react";
import { useForm } from "react-hook-form";
import { Modal, Toast } from "react-bootstrap"; // assuming you have this
import Datepicker from "react-tailwindcss-datepicker";
import { debounce } from "../../utils/Debounce";

// Reusable Form Component (used in drawer AND in "Choose For All")
const CategoryUpdateForm = ({
  handleSubmit,
  register,
  errors,
  isSubmitting,
  watch,
  setValue,
  categories,
  subCategoriesMain,
}) => {
  const selectedCategory = watch("category");

  const filteredSubCategories = subCategoriesMain.filter(
    (sub) => +selectedCategory === +sub.main_id
  );

  useEffect(() => {
    setValue("subcategory", "");
  }, [selectedCategory, setValue]);

  return (
    <form onSubmit={handleSubmit}>
      {/* CATEGORY - now properly pre-selected */}
      <div className="mb-3">
        <label className="form-label">Category</label>
        <select
          className="form-select"
          {...register("category", { required: true })}
          value={watch("category") || ""}
          onChange={(e) =>
            setValue("category", e.target.value, { shouldValidate: true })
          }
        >
          <option value="">choose</option>
          {categories.map((item) => (
            <option key={item.category_id} value={item.category_id}>
              {item.category_name}
            </option>
          ))}
        </select>
        {errors.category && (
          <div className="text-danger">Category is required</div>
        )}
      </div>

      {/* SUBCATEGORY - searchable + working */}
      <div className="mb-3">
        <label className="form-label">Subcategory</label>
        <SlSelect
          placeholder="Search subcategory..."
          clearable
          {...register("subcategory", { required: true })}
          value={watch("subcategory") || ""}
          onSlChange={(e) =>
            setValue("subcategory", e.target.value, { shouldValidate: true })
          }
          onSlClear={() =>
            setValue("subcategory", "", { shouldValidate: true })
          }
          disabled={!selectedCategory}
        >
          {filteredSubCategories.map((item) => (
            <SlOption key={item.category_id} value={item.category_id}>
              {item.category_name}
            </SlOption>
          ))}
        </SlSelect>
        {errors.subcategory && (
          <div className="text-danger">Subcategory is required</div>
        )}
      </div>

      <SlButton type="submit" variant="primary" disabled={isSubmitting}>
        {isSubmitting ? "Updating..." : "Submit"}
      </SlButton>
    </form>
  );
};
function CategoryUpdater() {
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategoriesMain, setSubCategoriesMain] = useState([]);
  const [item, setItem] = useState({});
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      category: "",
      subcategory: "",
    },
  });

  const getData = async (date = "", page = 0, search = "") => {
    try {
      setLoader(true);
      const result = await API({
        url: "/lineitems/getCategoryData",
        method: "post",
        data: {
          dateRange: date,
          page,
          search,
          limit: config.PER_PAGE,
        },
      });

      if (result.code === 200) {
        setData(result.data.data);
        setTotalPages(Math.ceil(parseInt(result.data.total) / config.PER_PAGE));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  const loadCategories = async () => {
    try {
      let res = await API({
        url: `/competitor_reports/getCategoriesAlpha`,
        method: "POST",
      });
      if (res?.code === 200) {
        setCategories(res?.data?.categories || []);
        setSubCategoriesMain(res?.data?.subCategories || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
    getData(dateRange, event.selected, search);
  };

  const handleDateRange = (selectedDate) => {
    setData([]);
    setCurrentPage(0);
    setSearch("");
    setDateRange(selectedDate || { startDate: null, endDate: null });
    getData(selectedDate, 0, "");
  };

  const handleEdit = (item) => {
    setItem(item);
    reset({
      category: item.category_id || "",
      subcategory: item.subcategory_id || "",
    });
    setIsEditing(true);
  };

  const handleClose = () => {
    setItem({});
    setIsEditing(false);
    reset({ category: "", subcategory: "" });
  };

  const onSubmit = async (formData) => {
    try {
      const payload = checked
        ? { ...formData, campaign_id: data.map((item) => item.campaign_id) } // bulk update → no campaign_id
        : { ...formData, campaign_id: [item.campaign_id] };
      if (!payload.campaign_id?.length) return;
      let res = await API({
        url: "/lineitems/category-update",
        method: "POST",
        data: payload,
      });

      if (res.code === 200) {
        handleClose();
        setChecked(false);
        getData(dateRange, currentPage, search);
      } else {
        // your original Toast usage
        Toast("error", "Something went wrong");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(0)
    getData(dateRange, 0, value);
  };

  useEffect(() => {
    getData();
    loadCategories();
  }, []);


  return (
    <div>
      <h5>Category Updater</h5>

      <aside className="flex gap-3 justify-end w-1/2 ml-auto">
        <div className="mb-3 w-1/2">
          <div className="border rounded-lg min-w-fit h-fit">
            <Datepicker
              value={dateRange}
              primaryColor={"cyan"}
              showShortcuts={true}
              useRange={true}
              configs={{
                shortcuts: {
                  today: "today",
                  yesterday: "yesterday",
                  currentMonth: "currentMonth",
                  pastMonth: "lastMonth",
                },
              }}
              onChange={handleDateRange}
            />
          </div>
          <SlInput
            label="Search Lineitem"
            type="text"
            value={search}
            placeholder="Search Lineitem"
            onSlInput={debounce(handleSearch, 500)}
          />
        </div>

        <div className="w-1/2">
          <SlCheckbox
            checked={checked}
            onSlChange={() => setChecked((prev) => !prev)}
          >
            Choose For all
          </SlCheckbox>

          {checked && (
            <div className="mt-3 p-4 border rounded bg-gray-50">
              <CategoryUpdateForm
                register={register}
                handleSubmit={handleSubmit(onSubmit)}
                errors={errors}
                isSubmitting={isSubmitting}
                watch={watch}
                categories={categories}
                subCategoriesMain={subCategoriesMain}
                setValue={setValue}
              />
            </div>
          )}
        </div>
      </aside>

      {/* TABLE */}
      <ScrollContainer>
        <table className="table table-bordered">
          <thead className="sticky top-0">
            <tr>
              <th>S.No</th>
              <th>Line Item Name</th>
              <th>Creative Name</th>
              <th>Actions</th>
            </tr>
          </thead>

          {loader ? (
            <TableLoader colspan="15" />
          ) : data?.length ? (
            <tbody>
              {data.map((item, index) => (
                <tr key={item.id || index}>
                  <td>{index + 1 + currentPage * config.PER_PAGE}</td>
                  <td>{item.line_item_name}</td>
                  <td>{item.creative_name}</td>
                  <td>
                    <SlIcon
                      name="pencil-square"
                      style={{ cursor: "pointer", fontSize: "18px" }}
                      onClick={() => handleEdit(item)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <NoDataFound colspan="15" />
          )}
        </table>
      </ScrollContainer>

      {totalPages > 1 && (
        <div className="right-absolute mt-3">
          <Paginator
            pageCount={totalPages}
            handlePageClick={handlePageClick}
            forcePage={currentPage}
          />
        </div>
      )}

      {/* DRAWER - Reuses same form */}
      <Modal show={isEditing} onHide={handleClose} size="xl">
        <Modal.Header closeButton>
          <span className="text-base font-medium">Update Categories</span>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="d-flex gap-4">
            <div style={{ width: "55%" }}>
              <h6>Preview</h6>
              <p>
                <b>Type:</b> {item.creative_type}
              </p>
              {item.img_urls ? (
                <img
                  src={item.img_urls}
                  alt="creative"
                  style={{ width: "100%", borderRadius: "6px" }}
                />
              ) : (
                <p>No Image</p>
              )}
            </div>

            <div style={{ width: "45%" }}>
              <CategoryUpdateForm
                register={register}
                handleSubmit={handleSubmit(onSubmit)}
                errors={errors}
                isSubmitting={isSubmitting}
                watch={watch}
                categories={categories}
                subCategoriesMain={subCategoriesMain}
                setValue={setValue}
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CategoryUpdater;
